import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {combineLatest, Subscription} from 'rxjs';

import {Job} from '../../../models';
import {
    AgentService,
    AuthenticationService,
    DialogService,
    InvoicesService,
    JobsService,
    SnackbarService
} from '../../../services';
import {AddEditInvoiceComponent} from '../../add-edit-invoice/add-edit-invoice.component';
import {NotifyAgentDialogComponent} from '../../notify-agent-dialog/notify-agent-dialog.component';
import {ConfirmationComponent} from '../confirmation/confirmation.component';

const BASE_COLUMNS = ['id', 'address', 'status', 'agent', 'agency', 'contractor', 'products', 'details', 'invoice'];
const MEMBER_COLUMN = 'member';
const TRAILING_COLUMNS = ['albums', 'share'];

@Component({
    selector: 'prism-jobs-table',
    templateUrl: './jobs-table.component.html',
    styleUrls: ['./jobs-table.component.scss']
})
export class JobsTableComponent implements OnInit, OnDestroy {
    member$ = this.authService.memberData$;
    isLuxEngine$ = this.authService.isLuxEngine$;
    private memberSub: Subscription;
    private paginator: MatPaginator;
    columnHeaders = [...BASE_COLUMNS, MEMBER_COLUMN, ...TRAILING_COLUMNS];
    jobsList = new MatTableDataSource<Job>([]);
    @Output() pageUpdated: EventEmitter<any> = new EventEmitter<any>();
    paginatorControl = {
        length: 0,
        pageSize: this.jobsService.jobsTableSize,
        pageSizeOptions: [10, 20, 40, 50, 100]
    };

    @Input() jobsLoading: boolean;

    @Input()
    set totalJobsForPaginator(jobsTotalCount) {
        if (jobsTotalCount > -1) {
            this.paginatorControl.length = jobsTotalCount;
            this.paginator.length = jobsTotalCount;
            this.paginator.firstPage();
        }
    }

    @Input() jobStatuses;

    @Input()
    set jobs(jobs: Job[]) {
        if (jobs) {
            this.jobsList.data = jobs.map(job => {
                return {
                    ...job,
                    productNames: !!job.productNames ? Object.values(job.productNames) : ['-']
                };
            });

        }
    }

    @Output()
    jobSelected = new EventEmitter<Job>();

    @ViewChild(MatPaginator, {static: true}) set matPaginator(mp: MatPaginator) {
        this.paginator = mp;
    }

    constructor(
        private router: Router,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private authService: AuthenticationService,
        private agentService: AgentService,
        private invoicesService: InvoicesService,
        private jobsService: JobsService
    ) {
    }

    ngOnInit() {
        this.memberSub = combineLatest([this.member$, this.isLuxEngine$])
            .subscribe(([member, isLuxEngine]) => {
                const baseColumns = isLuxEngine ? BASE_COLUMNS.filter(col => col !== 'contractor') : BASE_COLUMNS;
                // LuxEngine members never see the Cost column, same as contractors.
                this.columnHeaders = (member?.isContractor || isLuxEngine)
                    ? [...baseColumns, ...TRAILING_COLUMNS]
                    : [...baseColumns, MEMBER_COLUMN, ...TRAILING_COLUMNS];
            });
    }

    ngOnDestroy() {
        this.memberSub?.unsubscribe();
    }

    openAlbum(job: Job) {
        this.selectJob(job);
        this.router.navigate(['home', 'jobs', job.id, 'album']);
    }

    selectJob(job: Job) {
        this.jobSelected.emit(job);
    }

    paginate(pageEvent: PageEvent) {
        const {pageSize, pageIndex} = pageEvent;
        this.jobsService.updateJobsListPagination({pageSize, pageIndex});
    }

    openConfirmationDialog(job: Job) {
        const ref = this.dialogService.openDialog(ConfirmationComponent, {job}, {width: '90vw'});
        ref.afterClosed().subscribe(result => {
            if (result) {
                this.selectJob(job);
            }
        });
    }

    openInvoiceDialog(job) {
        this.jobsService.setSelectedJob(job);
        this.dialogService.openDialog(AddEditInvoiceComponent, {job}, {width: '90vw'});
    }

    shareJobAlbum(job: Job) {
        this.dialogService.openDialog(NotifyAgentDialogComponent, {job}, {width: '600px', autoFocus: false});
    }
}
